import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { BrandMark } from "./ui-kit";
import {
  CarProfile,
  ClockClockwise,
  CreditCard,
  Database,
  FileText,
  Gauge,
  List,
  Megaphone,
  RocketLaunch,
  ShieldWarning,
  SignOut,
  SlidersHorizontal,
  Storefront,
  UserCircle,
  UsersThree,
  SquaresFour,
  X,
} from "@phosphor-icons/react";

const ADMIN_GROUPS = [
  {
    labelKey: "admin.group_control",
    items: [
      { to: "/admin", labelKey: "admin.dashboard", icon: Gauge, exact: true, access: ["admin"] },
      { to: "/admin/overview", labelKey: "admin.overview", icon: SquaresFour, access: ["admin"] },
      { to: "/admin/launch", labelKey: "admin.launch", icon: RocketLaunch, access: ["admin"] },
      { to: "/admin/platform", label: "Plateforme", icon: Database, access: ["admin", "settings"] },
    ],
  },
  {
    labelKey: "admin.group_ops",
    items: [
      { to: "/admin/rides", labelKey: "admin.rides", icon: CarProfile, access: ["admin", "support", "delivery_ops"] },
      { to: "/admin/drivers", labelKey: "admin.drivers", icon: CarProfile, access: ["admin", "driver_onboarding"] },
      { to: "/admin/users", labelKey: "admin.users", icon: UsersThree, access: ["admin"] },
      { to: "/admin/documents", labelKey: "admin.documents", icon: FileText, access: ["admin", "driver_onboarding"] },
      { to: "/admin/events", labelKey: "admin.events", icon: SquaresFour, access: ["admin", "settings"] },
      { to: "/admin/marketplace", label: "Marketplace", icon: Storefront, access: ["admin", "marketplace", "merchant_onboarding"] },
    ],
  },
  {
    labelKey: "admin.group_money",
    items: [
      { to: "/admin/payments", labelKey: "admin.payments", icon: CreditCard, access: ["admin", "finance"] },
      { to: "/admin/finance", labelKey: "admin.finance", icon: CreditCard, access: ["admin", "finance"] },
      { to: "/admin/pricing", labelKey: "admin.pricing", icon: SlidersHorizontal, access: ["admin", "settings", "finance"] },
    ],
  },
  {
    labelKey: "admin.group_trust",
    items: [
      { to: "/admin/disputes", labelKey: "admin.disputes", icon: ShieldWarning, access: ["admin", "support"] },
      { to: "/admin/sos", labelKey: "admin.sos", icon: ShieldWarning, access: ["admin", "support"] },
      { to: "/admin/audit", labelKey: "admin.audit", icon: ClockClockwise, access: ["admin"] },
    ],
  },
  {
    labelKey: "admin.group_growth",
    items: [
      { to: "/admin/ads", labelKey: "admin.ads", icon: Megaphone, access: ["admin", "ads"] },
      { to: "/admin/settings", labelKey: "admin.settings", icon: SlidersHorizontal, access: ["admin", "settings"] },
      { to: "/admin/profile", labelKey: "admin.profile", icon: UserCircle, access: ["admin", "finance", "support", "marketplace", "delivery_ops", "driver_onboarding", "merchant_onboarding", "ads", "settings"] },
    ],
  },
];

function isActiveRoute(pathname, item) {
  if (item.exact) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function accessSetForUser(user) {
  const modules = new Set(Array.isArray(user?.access_modules) ? user.access_modules : []);
  if (user?.role) modules.add(user.role);
  return modules;
}

function canSeeItem(user, item) {
  const modules = accessSetForUser(user);
  return item.access?.some((access) => modules.has(access));
}

export default function AdminShell({ children, title }) {
  const loc = useLocation();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const visibleGroups = useMemo(() => ADMIN_GROUPS
    .map((group) => ({ ...group, items: group.items.filter((item) => canSeeItem(user, item)) }))
    .filter((group) => group.items.length), [user]);

  const activeItem = useMemo(() => {
    for (const group of visibleGroups.length ? visibleGroups : ADMIN_GROUPS) {
      const item = group.items.find((candidate) => isActiveRoute(loc.pathname, candidate));
      if (item) return { ...item, groupLabelKey: group.labelKey };
    }
    return ADMIN_GROUPS[0].items[0];
  }, [loc.pathname, visibleGroups]);

  return (
    <div className="app-screen overflow-x-hidden bg-premium-grad text-foreground">
      <header className="admin-header-safe sticky top-0 z-40 border-b border-white/10 bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-3 py-3 sm:px-6 sm:py-4">
          <div className="flex min-w-0 items-center gap-3 sm:gap-4">
            <Link to="/" className="public-header-brand min-w-0" data-testid="brand-link"><BrandMark /></Link>
            <div className="hidden min-w-0 sm:block">
              <div className="truncate text-xs font-bold uppercase tracking-widest text-muted-foreground">
                {title || t(activeItem?.groupLabelKey || "admin.group_control")}
              </div>
              <div className="truncate font-heading text-lg font-black">
                {activeItem?.label || t(activeItem?.labelKey || "admin.dashboard")}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {user && (
              <span className="hidden max-w-[180px] truncate text-xs font-bold uppercase tracking-widest text-muted-foreground lg:block" data-testid="user-name">
                {user.name}
              </span>
            )}
            <button
              type="button"
              onClick={() => setMenuOpen((value) => !value)}
              className="inline-flex min-h-10 items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-primary press-down hover:bg-primary/15"
              data-testid="admin-menu-toggle"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X size={16} weight="bold" /> : <List size={16} weight="bold" />}
              <span>{t("admin.menu")}</span>
            </button>
            <button
              onClick={logout}
              className="rounded-full border border-white/10 bg-white/5 p-2 press-down hover:bg-white/10"
              data-testid="logout-button"
              title="Deconnexion"
            >
              <SignOut size={18} weight="bold" />
            </button>
          </div>
        </div>
        <div className="border-t border-white/5 px-3 py-2 sm:hidden">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {t(activeItem?.groupLabelKey || "admin.group_control")}
              </div>
              <div className="truncate font-heading text-base font-black">
                {activeItem?.label || t(activeItem?.labelKey || "admin.dashboard")}
              </div>
            </div>
          </div>
        </div>
      </header>

      <nav className={`${menuOpen ? "block" : "hidden"} admin-tabs-safe sticky z-30 border-b border-white/10 bg-background/90 backdrop-blur-xl lg:block`} data-testid="admin-menu">
        <div className="mx-auto grid w-full max-w-7xl gap-3 px-3 py-3 sm:px-6 lg:grid-cols-5">
          {(visibleGroups.length ? visibleGroups : ADMIN_GROUPS).map((group) => (
            <div key={group.labelKey} className="rounded-2xl border border-white/10 bg-white/[0.03] p-2">
              <div className="px-2 pb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                {t(group.labelKey)}
              </div>
              <div className="grid gap-1">
                {group.items.map((item) => {
                  const active = isActiveRoute(loc.pathname, item);
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setMenuOpen(false)}
                      className={`flex min-h-11 min-w-0 items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition-colors ${
                        active
                          ? "bg-primary text-primary-foreground glow-blue"
                          : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
                      }`}
                      data-testid={`admin-nav-${item.to.replace(/[^a-z]/g, "")}`}
                    >
                      <Icon size={17} weight="bold" className="shrink-0" />
                      <span className="truncate">{item.label || t(item.labelKey)}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </nav>

      <main className="mx-auto w-full max-w-7xl px-3 py-4 pb-10 sm:px-6 sm:py-6 lg:px-8">{children}</main>
    </div>
  );
}
